import { useRequest } from 'ahooks'
import { Table } from 'antd'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { getNameLink } from '@/components/special-project-relations.component'
import { ProjectWeekWorkResourceSnapshotType, ResourceType } from '@/schemas'

interface ProjectDetail {
  project: { name: string; selfId: string; resourceType: ProjectWeekWorkResourceSnapshotType }
  weeks: { year: string; week: string; workload: number; percentage: number }[]
  specificProjects: { selfId: string; name: string; resourceType: ResourceType; proportion: number }[]
}

export const TeamProjectDetailTable: React.FC<{ departmentId: number }> = ({ departmentId }) => {
  const { t } = useTranslation()

  const startDate = new URLSearchParams(window.location.search).get('startDate')
  const endDate = new URLSearchParams(window.location.search).get('endDate')

  const { data, run } = useRequest(
    {
      url: '/api/work-resource/statistics/team/detail/project/infos',
      params: {
        startDate,
        endDate,
        departmentId,
      },
    },
    {
      manual: true,
      initialData: [] as ProjectDetail[],
    }
  )

  React.useEffect(() => {
    if (departmentId && startDate && endDate) {
      run()
    }
  }, [departmentId, startDate, endDate])

  const columns: any[] = [
    {
      title: t('dashboard.table.normalProjectCol'),
      dataIndex: 'title',
      width: 200,
      fixed: 'left',
      render: (_: any, record: ProjectDetail) => {
        if (record.project.name === 'Total') {
          return t('user resource total')
        }
        return record.project.resourceType === ProjectWeekWorkResourceSnapshotType.PROJECT
          ? getNameLink({
              name: record.project.name,
              type: ResourceType.PROJECT,
              selfId: record.project.selfId,
            })
          : record.project.name
      },
    },
    {
      title: t('dashboard.table.specificProjectCol'),
      dataIndex: 'specific-project',
      key: `specific-project-name`,
      width: 200,
      fixed: 'left',
      render: (_: any, record: ProjectDetail) => {
        const specificProjects = record.specificProjects || []
        return (
          <ul>
            {specificProjects.map((s) => (
              <li key={s.selfId}>
                {getNameLink({
                  name: s.name,
                  selfId: s.selfId,
                  type: s.resourceType,
                })}
                {specificProjects.length > 1 && <span className='ml-2'>{(s.proportion * 100).toFixed(1)}%</span>}
              </li>
            ))}
          </ul>
        )
      },
    },
  ]

  const _weeks = data!.find((d) => !!d.weeks.length)?.weeks
  _weeks &&
    _weeks.forEach((week: any) => {
      columns.push({
        title: `${week.year}.${week.week} ${t('week')}`,
        children: [
          {
            title: t('dashboard.table.workloadCol'),
            key: `${week.year}.${week.week}.workload`,
            width: 120,
            render: (_: any, record: ProjectDetail) => {
              return Number(
                record.weeks.find((w) => w.year === week.year && w.week === week.week)?.workload ?? 0
              ).toFixed(1)
            },
          },
          {
            title: t('dashboard.table.percentageCol'),
            key: `${week.year}.${week.week}.percentage`,
            width: 120,
            render: (_: any, record: ProjectDetail) => {
              const percentage = record.weeks.find((w: any) => w.year === week.year && w.week === week.week)?.percentage
              return `${percentage ? (Number(percentage) * 100).toFixed(1) : 0}%`
            },
          },
        ],
      })
    })

  columns.push({
    title: t('user resource total'),
    children: [
      {
        title: t('dashboard.table.totalWorkloadCol'),
        key: 'totalWorkload',
        fixed: 'right',
        dataIndex: 'workload',
        width: 100,
        render: (value: any, _: any) => Number(value || 0).toFixed(1),
      },
      {
        title: t('dashboard.table.totalPercentageCol'),
        key: 'percentage',
        fixed: 'right',
        width: 100,
        render: (_: any, record: any) => {
          return `${(Number(record.percentage) * 100).toFixed(1)}%`
        },
      },
    ],
  })

  return <Table columns={columns} dataSource={data} pagination={false} scroll={{ x: 'fit-content', y: 600 }} />
}
